import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { SendHorizonal } from 'lucide-react'

import { PageContainer } from '@/components/common/page-container'
import { PageHeader } from '@/components/common/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { Pagination } from '@/components/ui/pagination'
import { MatchTable } from '@/features/matches/match-table'
import { MatchDrawer } from '@/features/matches/match-drawer'
import { APPLIED_STATUSES, matchesListQuery, type Match } from '@/api/matches'
import { RouteError } from '@/components/common/route-error'

/**
 * /applications — Q6c carve-out: only matches we have applied to
 * (auto OR mark-applied). Same endpoint as /matches; we push the
 * applied-status CSV down to the server so pagination is honest
 * (P1.6). Local `appliedAt` overlay still decorates rows — backend
 * `overlayApplied` runs after the upstream fetch (handlers/matches.go).
 */

const DEFAULT_PER_PAGE = 100
const APPLIED_FILTER = { status: APPLIED_STATUSES } as const

export const Route = createFileRoute('/_authed/applications')({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(
      matchesListQuery(1, DEFAULT_PER_PAGE, APPLIED_FILTER),
    ),
  component: ApplicationsPage,
  errorComponent: RouteError,
})

function ApplicationsPage() {
  const [page, setPage] = useState(1)
  const { data } = useSuspenseQuery(
    matchesListQuery(page, DEFAULT_PER_PAGE, APPLIED_FILTER),
  )
  const [selected, setSelected] = useState<Match | null>(null)

  return (
    <PageContainer>
      <PageHeader
        title="Applications"
        description={`Jobs you've applied to — auto-applied through Fuku or marked as applied after clicking through. ${data.total} total.`}
      />

      {data.items.length === 0 ? (
        <EmptyState
          icon={SendHorizonal}
          title="No applications yet"
          description="Apply to a match (or mark one as applied externally) and it lands here."
        />
      ) : (
        <MatchTable
          items={data.items}
          selectedId={selected?.id}
          onSelect={(m) => setSelected(m)}
        />
      )}

      <Pagination
        page={page}
        perPage={DEFAULT_PER_PAGE}
        total={data.total}
        onPageChange={(p) => {
          setPage(p)
          setSelected(null)
        }}
        className="mt-4"
      />

      <MatchDrawer
        matchId={selected?.id ?? null}
        open={!!selected}
        onClose={() => setSelected(null)}
      />
    </PageContainer>
  )
}
